import Navbar from "@/components/Navbar";
import Messages from "@/components/Messages";
import { Overline } from "@/components/shared";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PayoutsTab } from "@/components/PayoutsTab";
import { SupportPanel } from "@/components/SupportPanel";
import { ReportsTab } from "@/components/ReportsTab";
import { AISuiteTab } from "@/components/AISuiteTab";
import { ExpensesTab } from "@/components/ExpensesTab";
import { ApplicationTemplateBuilder } from "@/components/ApplicationTemplateBuilder";
import { PropertiesTab } from "@/pages/landlord/PropertiesTab";
import { ApplicationsTab } from "@/pages/landlord/ApplicationsTab";
import { ShowingsTab } from "@/pages/landlord/ShowingsTab";
import { LeasesTab } from "@/pages/landlord/LeasesTab";
import { MaintenanceTab } from "@/pages/landlord/MaintenanceTab";
import { LeaseFormDialog, DeclineDialog } from "@/pages/landlord/LeaseDialogs";
import { SuggestPropertiesDialog } from "@/pages/landlord/SuggestPropertiesDialog";
import { useLandlordDashboard } from "@/pages/landlord/useLandlordDashboard";
import {
  FileText, CalendarCheck, Wrench, MessageSquare, AlertTriangle,
  ScrollText, LayoutDashboard, Wallet, LifeBuoy, BarChart3, Sparkles, Receipt,
} from "lucide-react";

export default function LandlordDashboard() {
  const d = useLandlordDashboard();
  const {
    user, properties, applications, showings, leases, maintenance,
    propForm, activeTab, setActiveTab, editId, propOpen, setPropOpen, busy,
    leaseForm, setLeaseForm, payout, paypalEmail, setPaypalEmail, venmoHandle, setVenmoHandle,
    couponCode, setCouponCode, billing, declineApp, setDeclineApp, unread, supportUnread,
    activity, contractors, autoPurgeDays, suggestApp, setSuggestApp,
    loadAll, loadUnread, loadSupportUnread, saveAutoPurge, savePayout, connectStripe,
    upgradePlan, redeemCoupon, cancelPlan, reactivatePlan, fmtDate,
    addContractor, deleteContractor, openCreate, openEdit, onImageFiles, removeImage,
    saveProperty, deleteProperty, toggleVisibility, markFilled, markAvailable, relistUnit,
    deleteApplication, bulkPurgeApplications, deleteSignedApplication, deleteSignedLeaseDoc,
    decide, openDecline, confirmDecline, respondShowing, updateMaint, dispatchMaint,
    resendDispatch, rateContractor,
    openLeaseFor, sendReminder, sendInvoice, markRentPaid, createLease,
    endLease, deleteLease, addLeaseDocuments, deleteLeaseInfoDoc,
    printApplication, KPIS, set, overdueApps, overdueLeaseDocs, overdueCount,
  } = d;

  return (
    <div className="min-h-screen bg-bone">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <Overline>Landlord workspace</Overline>
        <h1 className="mt-2 text-3xl font-semibold tracking-tight text-ink">Welcome, {user.name.split(" ")[0]}</h1>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          {KPIS.map((k) => (
            <button
              key={k.label}
              data-testid={`kpi-card-${k.tab}`}
              onClick={() => setActiveTab(k.tab)}
              className="text-left bg-white border border-line rounded-xl p-5 hover:border-forest/40 hover:shadow-sm transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-forest/20"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm text-mute">{k.label}</span>
                <k.icon className="h-4 w-4 text-forest" strokeWidth={1.75} />
              </div>
              <p data-testid={`kpi-${k.label}`} className="mt-2 text-2xl font-heading font-semibold text-ink">{k.value}</p>
            </button>
          ))}
        </div>

        {overdueCount > 0 && (
          <div data-testid="purge-reminder-banner" className="mt-6 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
            <AlertTriangle className="h-5 w-5 text-amber-700 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-ink">{overdueCount} signed document{overdueCount > 1 ? "s have" : " has"} been stored on the platform for 30+ days.</p>
              <p className="text-xs text-mute">For your tenants' security, download or print what you need, then remove them from the platform.</p>
            </div>
            <div className="flex gap-2 shrink-0">
              {overdueApps.length > 0 && <button data-testid="purge-review-apps" onClick={() => setActiveTab("applications")} className="text-xs border border-amber-300 text-amber-800 rounded-lg px-2.5 py-1.5 hover:bg-amber-100">Review applications ({overdueApps.length})</button>}
              {overdueLeaseDocs.length > 0 && <button data-testid="purge-review-leases" onClick={() => setActiveTab("leases")} className="text-xs border border-amber-300 text-amber-800 rounded-lg px-2.5 py-1.5 hover:bg-amber-100">Review leases ({overdueLeaseDocs.length})</button>}
            </div>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-8">
          <TabsList className="bg-white border border-line rounded-xl p-1 h-auto flex flex-nowrap overflow-x-auto no-scrollbar md:flex-wrap justify-start gap-1">
            {[
              ["properties", "Properties", LayoutDashboard],
              ["applications", "Applications", FileText],
              ["showings", "Showings", CalendarCheck],
              ["leases", "Leases", ScrollText],
              ["app-form", "Application Form", FileText],
              ["maintenance", "Maintenance", Wrench],
              ["payouts", "Payouts", Wallet],
              ["reports", "Reports", BarChart3],
              ["ai", "AI Suite", Sparkles],
              ["expenses", "Expenses", Receipt],
              ["messages", "Messages", MessageSquare],
              ["support", "Support", LifeBuoy],
            ].map(([v, label, Icon]) => (
              <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="shrink-0 data-[state=active]:bg-forest data-[state=active]:text-white rounded-lg px-4 py-2 text-sm gap-2">
                <Icon className="h-4 w-4" /> {label}
                {v === "messages" && unread > 0 && (
                  <span data-testid="messages-unread-badge" className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-terracotta text-white text-[11px] font-semibold">{unread}</span>
                )}
                {v === "support" && supportUnread > 0 && (
                  <span data-testid="support-unread-badge" className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-terracotta text-white text-[11px] font-semibold">{supportUnread}</span>
                )}
                {v !== "messages" && v !== "support" && (activity[v] || 0) > 0 && (
                  <span data-testid={`tab-badge-${v}`} className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-terracotta text-white text-[11px] font-semibold">{activity[v]}</span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="properties" className="mt-6">
            <PropertiesTab
              properties={properties} payout={payout} setActiveTab={setActiveTab}
              propOpen={propOpen} setPropOpen={setPropOpen} openCreate={openCreate} openEdit={openEdit}
              editId={editId} propForm={propForm} set={set} onImageFiles={onImageFiles}
              removeImage={removeImage} saveProperty={saveProperty} busy={busy}
              deleteProperty={deleteProperty} toggleVisibility={toggleVisibility} markFilled={markFilled} markAvailable={markAvailable} reload={loadAll}
            />
          </TabsContent>

          <TabsContent value="applications" className="mt-6">
            <ApplicationsTab
              applications={applications} properties={properties} leases={leases} decide={decide}
              printApplication={printApplication} openDecline={openDecline} openLeaseFor={openLeaseFor}
              deleteApplication={deleteApplication} deleteSignedApplication={deleteSignedApplication}
              bulkPurgeApplications={bulkPurgeApplications}
              autoPurgeDays={autoPurgeDays} saveAutoPurge={saveAutoPurge}
              onSuggest={setSuggestApp}
            />
          </TabsContent>

          <TabsContent value="app-form" className="mt-6">
            <ApplicationTemplateBuilder />
          </TabsContent>

          <TabsContent value="showings" className="mt-6">
            <ShowingsTab showings={showings} respondShowing={respondShowing} />
          </TabsContent>

          <TabsContent value="leases" className="mt-6">
            <LeasesTab leases={leases} sendInvoice={sendInvoice} sendReminder={sendReminder} deleteSignedLeaseDoc={deleteSignedLeaseDoc} markRentPaid={markRentPaid} endLease={endLease} deleteLease={deleteLease} relistUnit={relistUnit} addLeaseDocuments={addLeaseDocuments} deleteLeaseInfoDoc={deleteLeaseInfoDoc} />
          </TabsContent>

          <TabsContent value="maintenance" className="mt-6">
            <MaintenanceTab maintenance={maintenance} updateMaint={updateMaint} dispatchMaint={dispatchMaint} resendDispatch={resendDispatch} rateContractor={rateContractor} contractors={contractors} addContractor={addContractor} deleteContractor={deleteContractor} isPro={payout.plan_tier === "pro"} />
          </TabsContent>

          <TabsContent value="messages" className="mt-6">
            <Messages onActivity={loadUnread} />
          </TabsContent>

          <TabsContent value="support" className="mt-6">
            <SupportPanel onActivity={loadSupportUnread} />
          </TabsContent>

          <TabsContent value="reports" className="mt-6">
            <ReportsTab isPro={payout.plan_tier === "pro"} onUpgrade={() => setActiveTab("payouts")} />
          </TabsContent>

          <TabsContent value="ai" className="mt-6">
            <AISuiteTab properties={properties} onUpgrade={() => setActiveTab("payouts")} />
          </TabsContent>

          <TabsContent value="expenses" className="mt-6">
            <ExpensesTab properties={properties} isPro={payout.plan_tier === "pro"} onUpgrade={() => setActiveTab("payouts")} />
          </TabsContent>

          <TabsContent value="payouts" className="mt-6">
            <PayoutsTab
              payout={payout} billing={billing} paypalEmail={paypalEmail} setPaypalEmail={setPaypalEmail}
              venmoHandle={venmoHandle} setVenmoHandle={setVenmoHandle}
              busy={busy} upgradePlan={upgradePlan} cancelPlan={cancelPlan} reactivatePlan={reactivatePlan}
              savePayout={savePayout} connectStripe={connectStripe} fmtDate={fmtDate}
              couponCode={couponCode} setCouponCode={setCouponCode} redeemCoupon={redeemCoupon}
            />
          </TabsContent>
        </Tabs>
      </div>

      <LeaseFormDialog leaseForm={leaseForm} setLeaseForm={setLeaseForm} busy={busy} createLease={createLease} />
      <DeclineDialog declineApp={declineApp} setDeclineApp={setDeclineApp} busy={busy} confirmDecline={confirmDecline} />
      <SuggestPropertiesDialog open={!!suggestApp} onOpenChange={(o) => !o && setSuggestApp(null)} application={suggestApp} properties={properties} onSent={loadAll} />
    </div>
  );
}
